<script setup lang="ts">
import { computed } from 'vue';

// Two shapes for the same control (packet suite-design-ats-M5-p2): a lone
// toggle (`v-model` on a boolean, unchanged) or one entry in a checkbox GROUP
// (`v-model` on an array shared by every Checkbox in the group, each given
// its own `value` — the same membership contract native
// `<input type="checkbox" v-model="arr" :value="x">` has always had, which a
// plain boolean-only primitive could not offer a workflow-form role/scope
// picker converting off hand-rolled `<input>` lists).
const props = withDefaults(
  defineProps<{
    label?: string;
    disabled?: boolean;
    value?: unknown;
    /** A tertiary second line under the label (workflow forms + the roles drawer,
     * users & assignments rework M2): a role's hint, shown at less weight than the
     * label itself. Prefer the `description` slot over the prop when the text needs
     * its own markup. */
    description?: string;
  }>(),
  { disabled: false },
);

// `any`, not `boolean | unknown[]`: a union here would widen the emitted
// `update:modelValue` callback type for every EXISTING boolean-only consumer
// across every app (apprentice-ui, time-ui, …), forcing edits far outside
// this packet's worktree. `any` keeps this component's own logic exact (see
// the `checked` computed below) while staying silently assignable both ways.
const model = defineModel<any>({ default: false });

const checked = computed<boolean>({
  get: () => (Array.isArray(model.value) ? model.value.includes(props.value) : !!model.value),
  set: (next) => {
    if (!Array.isArray(model.value)) {
      model.value = next;
      return;
    }
    const arr = model.value.slice();
    const idx = arr.indexOf(props.value);
    if (next && idx === -1) arr.push(props.value);
    if (!next && idx !== -1) arr.splice(idx, 1);
    model.value = arr;
  },
});
</script>

<template>
  <label class="lumi-check" :class="{ 'lumi-check--disabled': disabled }">
    <input v-model="checked" type="checkbox" :disabled="disabled" />
    <span v-if="label || $slots.default || description || $slots.description" class="lumi-check__body">
      <span class="lumi-check__label"><slot>{{ label }}</slot></span>
      <span v-if="description || $slots.description" class="lumi-check__description">
        <slot name="description">{{ description }}</slot>
      </span>
    </span>
  </label>
</template>

<style>
.lumi-check{display:inline-flex;align-items:flex-start;gap:10px;cursor:pointer;font-family:var(--font-body);font-size:var(--text-sm);color:var(--text-body);}
/* Native box, ink accent: espresso in light, sand in dark (the primary colour). */
.lumi-check input{width:16px;height:16px;margin:2px 0 0;accent-color:var(--btn-primary-bg);cursor:pointer;flex-shrink:0;}
.lumi-check__body{display:flex;flex-direction:column;gap:2px;}
/* Tertiary hint line (a role's description in a checkbox grid): quieter than the
   label, never wraps into it. */
.lumi-check__description{font-size:var(--text-xs);color:var(--text-tertiary);}
.lumi-check--disabled{opacity:.5;cursor:not-allowed;}
.lumi-check--disabled input{cursor:not-allowed;}
</style>
